import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const slides = [
  {
    id: 1,
    bg: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    accent: "#4ade80",
    tag: "WELCOME TO",
    headline: ["India's #1", "Energy", "Marketplace"],
    sub: "One platform. 10,000+ products. 500+ verified vendors. Zero commission.",
    visual: "intro",
    duration: 3000,
  },
  {
    id: 2,
    bg: "linear-gradient(135deg, #064e3b 0%, #065f46 100%)",
    accent: "#34d399",
    tag: "STEP 01 — BROWSE",
    headline: ["Find Every", "Energy Product", "You Need"],
    sub: "EV Chargers · Solar Panels · Batteries · IoT Devices · Cables · Vehicles",
    visual: "browse",
    duration: 3200,
  },
  {
    id: 3,
    bg: "linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)",
    accent: "#60a5fa",
    tag: "STEP 02 — ENQUIRE",
    headline: ["Get Quotes", "In Minutes,", "Not Days"],
    sub: "Send enquiries to multiple vendors at once. Compare. Decide. Save time.",
    visual: "enquire",
    duration: 3200,
  },
  {
    id: 4,
    bg: "linear-gradient(135deg, #3b0764 0%, #4c1d95 100%)",
    accent: "#c084fc",
    tag: "STEP 03 — CONNECT",
    headline: ["Verified", "Vendors.", "Real Results."],
    sub: "Every vendor is KYC verified. Every product is quality checked. No surprises.",
    visual: "connect",
    duration: 3200,
  },
  {
    id: 5,
    bg: "linear-gradient(135deg, #7c2d12 0%, #9a3412 100%)",
    accent: "#fb923c",
    tag: "STEP 04 — EXECUTE",
    headline: ["From Order", "To Installation,", "We're With You"],
    sub: "EPC partners · Electricians · AMC services · Remote monitoring — all in one place.",
    visual: "execute",
    duration: 3200,
  },
  {
    id: 6,
    bg: "linear-gradient(135deg, #0f172a 0%, #16213e 100%)",
    accent: "#4ade80",
    tag: "YOU'RE READY",
    headline: ["Start Your", "Green Energy", "Journey Today"],
    sub: "Join 10,000+ businesses already powered by EcoConnect.",
    visual: "cta",
    duration: 99999,
  },
];

const categories = [
  { icon: "⚡", label: "EV Chargers", color: "#4ade80" },
  { icon: "☀️", label: "Solar Panels", color: "#fbbf24" },
  { icon: "🔋", label: "Batteries", color: "#a78bfa" },
  { icon: "📡", label: "IoT Devices", color: "#60a5fa" },
  { icon: "🔌", label: "Cables", color: "#34d399" },
  { icon: "🛵", label: "Vehicles", color: "#fb923c" },
];

const vendors = ["Mindra", "Tata Power", "Waaree", "Loom Solar", "Exide", "Havells"];

function IntroVisual({ active }) {
  return (
    <div style={{ position: "relative", width: 340, height: 220 }}>
      {/* Orbiting rings */}
      {[0,1,2].map(i => (
        <div key={i} style={{
          position: "absolute",
          width: 80 + i*80, height: 80 + i*80,
          left: "50%", top: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          border: `1px solid rgba(74,222,128,${0.3 - i*0.08})`,
          animation: `spin ${8 + i*4}s linear infinite`,
        }} />
      ))}
      {/* Center */}
      <div style={{
        position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
        width: 72, height: 72, borderRadius: "50%",
        background: "linear-gradient(135deg, #4ade80, #16a34a)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 40px rgba(74,222,128,0.4)",
        fontSize: 28,
        animation: "pulse 2s ease-in-out infinite",
      }}>
        ⚡
      </div>
      {/* Floating stats */}
      {[
        { label: "10K+", sub: "Products", top: "10%", left: "0%" },
        { label: "500+", sub: "Vendors", top: "10%", right: "0%" },
        { label: "₹0", sub: "Commission", bottom: "10%", left: "5%" },
        { label: "48+", sub: "Cities", bottom: "10%", right: "5%" },
      ].map((s, i) => (
        <div key={i} style={{
          position: "absolute", ...s,
          background: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 10, padding: "8px 14px", textAlign: "center",
          animation: `floatUp 0.6s ease ${i * 0.15}s both`,
        }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 900, color: "#4ade80" }}>{s.label}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>{s.sub}</div>
        </div>
      ))}
    </div>
  );
}

function BrowseVisual() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, width: 320 }}>
      {categories.map((c, i) => (
        <div key={i} style={{
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 14, padding: "16px 10px", textAlign: "center",
          animation: `floatUp 0.5s ease ${i * 0.1}s both`,
          cursor: "default",
        }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>{c.icon}</div>
          <div style={{ fontSize: 11, color: c.color, fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

function EnquireVisual() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % 4), 700);
    return () => clearInterval(t);
  }, []);

  const steps = [
    { icon: "🔍", label: "Browse Product", done: step >= 1 },
    { icon: "📋", label: "Send Enquiry", done: step >= 2 },
    { icon: "💬", label: "Vendor Responds", done: step >= 3 },
    { icon: "✅", label: "Deal Done!", done: step >= 4 },
  ];

  return (
    <div style={{ width: 320 }}>
      {steps.map((s, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "12px 16px", marginBottom: 8,
          background: s.done ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.05)",
          border: `1px solid ${s.done ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.08)"}`,
          borderRadius: 12, transition: "all 0.4s ease",
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: s.done ? "#4ade80" : "rgba(255,255,255,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, transition: "all 0.4s",
          }}>
            {s.done ? "✓" : s.icon}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: s.done ? "#4ade80" : "rgba(255,255,255,0.5)", fontFamily: "'DM Sans',sans-serif", transition: "color 0.4s" }}>
            {s.label}
          </span>
          {step === i && <div style={{ marginLeft: "auto", width: 8, height: 8, borderRadius: "50%", background: "#60a5fa", animation: "pulse 1s infinite" }} />}
        </div>
      ))}
    </div>
  );
}

function ConnectVisual() {
  return (
    <div style={{ width: 320 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {vendors.map((v, i) => (
          <div key={i} style={{
            padding: "8px 16px",
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(192,132,252,0.3)",
            borderRadius: 20, fontSize: 12, color: "rgba(255,255,255,0.8)",
            fontWeight: 600, fontFamily: "'Sora',sans-serif",
            animation: `floatUp 0.4s ease ${i * 0.08}s both`,
          }}>
            ✓ {v}
          </div>
        ))}
      </div>
      <div style={{ background: "rgba(192,132,252,0.1)", border: "1px solid rgba(192,132,252,0.2)", borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ fontSize: 11, color: "#c084fc", fontWeight: 800, letterSpacing: 1, marginBottom: 8 }}>KYC VERIFIED</div>
        <div style={{ display: "flex", gap: 16 }}>
          {["ID Check ✓", "GST Valid ✓", "Quality Rated ✓"].map((t, i) => (
            <span key={i} style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontFamily: "'DM Sans',sans-serif" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExecuteVisual() {
  const services = [
    { icon: "🔧", label: "Installation", color: "#fb923c" },
    { icon: "📊", label: "Monitoring", color: "#fb923c" },
    { icon: "🛠️", label: "Maintenance", color: "#fb923c" },
    { icon: "📍", label: "On-site Support", color: "#fb923c" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, width: 300 }}>
      {services.map((s, i) => (
        <div key={i} style={{
          background: "rgba(251,146,60,0.1)", border: "1px solid rgba(251,146,60,0.2)",
          borderRadius: 14, padding: "18px 16px", textAlign: "center",
          animation: `floatUp 0.5s ease ${i * 0.12}s both`,
        }}>
          <div style={{ fontSize: 26, marginBottom: 8 }}>{s.icon}</div>
          <div style={{ fontSize: 12, color: s.color, fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function CtaVisual({ onClose, navigate }) {
  const roles = [
    { emoji: "🛒", title: "I'm a Buyer", color: "#4ade80", path: "/products" },
    { emoji: "🏭", title: "I'm a Vendor", color: "#60a5fa", path: "/contact" },
    { emoji: "🤝", title: "I'm a Partner", color: "#c084fc", path: "/contact" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 300 }}>
      {roles.map((r, i) => (
        <button key={i}
          onClick={() => { onClose(); navigate(r.path); }}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 20px",
            background: "rgba(255,255,255,0.06)",
            border: `1.5px solid ${r.color}40`,
            borderRadius: 14, cursor: "pointer",
            animation: `floatUp 0.5s ease ${i * 0.12}s both`,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = `${r.color}15`; e.currentTarget.style.borderColor = r.color; e.currentTarget.style.transform = "translateX(4px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = `${r.color}40`; e.currentTarget.style.transform = "translateX(0)"; }}
        >
          <span style={{ fontSize: 22 }}>{r.emoji}</span>
          <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, color: "white" }}>{r.title}</span>
          <span style={{ marginLeft: "auto", color: r.color, fontSize: 16 }}>→</span>
        </button>
      ))}
    </div>
  );
}

export default function PlatformTour({ onClose }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const progressRef = useRef(null);
  const slideRef = useRef(null);

  const slide = slides[current];
  const isLast = current === slides.length - 1;

  useEffect(() => {
    if (isLast) return;
    setProgress(0);
    setTextVisible(false);
    setTimeout(() => setTextVisible(true), 100);

    let start = null;
    const duration = slide.duration;
    const animate = (ts) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      setProgress(Math.min((elapsed / duration) * 100, 100));
      if (elapsed < duration) {
        progressRef.current = requestAnimationFrame(animate);
      } else {
        setCurrent(c => c + 1);
      }
    };
    progressRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(progressRef.current);
  }, [current]);

  const goTo = (i) => {
    cancelAnimationFrame(progressRef.current);
    setCurrent(i);
  };

  const visuals = {
    intro: <IntroVisual />,
    browse: <BrowseVisual />,
    enquire: <EnquireVisual />,
    connect: <ConnectVisual />,
    execute: <ExecuteVisual />,
    cta: <CtaVisual onClose={onClose} navigate={navigate} />,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.85)",
      backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div ref={slideRef} style={{
        width: "100%", maxWidth: 880,
        background: slide.bg,
        borderRadius: 28,
        overflow: "hidden",
        boxShadow: "0 40px 100px rgba(0,0,0,0.5)",
        animation: "slideIn 0.4s cubic-bezier(0.34,1.3,0.64,1)",
        position: "relative",
      }}>

        {/* Progress bars */}
        <div style={{ display: "flex", gap: 4, padding: "16px 24px 0" }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => goTo(i)}
              style={{ flex: 1, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.15)", cursor: "pointer", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2,
                background: slide.accent,
                width: i < current ? "100%" : i === current ? `${progress}%` : "0%",
                transition: i === current ? "none" : "width 0.3s",
              }} />
            </div>
          ))}
        </div>

        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 24px" }}>
          <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 11, fontWeight: 800, color: slide.accent, letterSpacing: 2 }}>
            ECOCONNECT
          </span>
          <button onClick={onClose}
            style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: 32, height: 32, color: "white", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            ×
          </button>
        </div>

        {/* Main content */}
        <div style={{ display: "flex", alignItems: "center", gap: 48, padding: "20px 48px 48px", minHeight: 360 }}>

          {/* Left — Text */}
          <div style={{ flex: 1 }}>
            <div style={{
              display: "inline-block", background: `${slide.accent}20`,
              border: `1px solid ${slide.accent}40`,
              borderRadius: 100, padding: "4px 14px", marginBottom: 20,
              opacity: textVisible ? 1 : 0, transform: textVisible ? "translateY(0)" : "translateY(10px)",
              transition: "all 0.5s ease 0.1s",
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: slide.accent, letterSpacing: 2 }}>{slide.tag}</span>
            </div>

            <div>
              {slide.headline.map((line, i) => (
                <div key={i} style={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: "clamp(28px, 4vw, 44px)",
                  fontWeight: 900, color: i === 1 ? slide.accent : "white",
                  lineHeight: 1.1, letterSpacing: "-1px",
                  opacity: textVisible ? 1 : 0,
                  transform: textVisible ? "translateY(0)" : "translateY(16px)",
                  transition: `all 0.5s ease ${0.2 + i * 0.1}s`,
                }}>
                  {line}
                </div>
              ))}
            </div>

            <p style={{
              color: "rgba(255,255,255,0.55)", fontSize: 14,
              fontFamily: "'DM Sans',sans-serif", lineHeight: 1.7,
              margin: "20px 0 32px", maxWidth: 380,
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? "translateY(0)" : "translateY(12px)",
              transition: "all 0.5s ease 0.5s",
            }}>
              {slide.sub}
            </p>

            {/* Nav buttons */}
            {!isLast && (
              <div style={{
                display: "flex", gap: 12,
                opacity: textVisible ? 1 : 0, transition: "opacity 0.5s ease 0.6s",
              }}>
                {current > 0 && (
                  <button onClick={() => goTo(current - 1)}
                    style={{ padding: "10px 20px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10, color: "white", fontSize: 13, fontWeight: 700, fontFamily: "'Sora',sans-serif", cursor: "pointer" }}>
                    ← Back
                  </button>
                )}
                <button onClick={() => goTo(current + 1)}
                  style={{ padding: "10px 24px", background: slide.accent, border: "none", borderRadius: 10, color: "#0f172a", fontSize: 13, fontWeight: 800, fontFamily: "'Sora',sans-serif", cursor: "pointer", boxShadow: `0 4px 20px ${slide.accent}50` }}>
                  {current === slides.length - 2 ? "Get Started →" : "Next →"}
                </button>
                <button onClick={onClose}
                  style={{ padding: "10px 16px", background: "transparent", border: "none", color: "rgba(255,255,255,0.35)", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                  Skip tour
                </button>
              </div>
            )}
          </div>

          {/* Right — Visual */}
          <div style={{
            flexShrink: 0,
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateX(0) scale(1)" : "translateX(20px) scale(0.95)",
            transition: "all 0.6s cubic-bezier(0.34,1.3,0.64,1) 0.2s",
          }}>
            {visuals[slide.visual]}
          </div>
        </div>

        {/* Slide counter */}
        <div style={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6 }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => goTo(i)}
              style={{ width: i === current ? 20 : 6, height: 6, borderRadius: 3, background: i === current ? slide.accent : "rgba(255,255,255,0.2)", cursor: "pointer", transition: "all 0.3s" }} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: scale(0.94) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes floatUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 20px rgba(74,222,128,0.3); } 50% { box-shadow: 0 0 40px rgba(74,222,128,0.6); } }
      `}</style>
    </div>
  );
}